/** X（旧 Twitter）の接続方法: 開発者サイトで自分のキー4つを発行して貼る */
import {
  BrowserFrame, Callout, Checklist, Code, Mark, MockButton, MockInput, MockRow, MockSidebarLayout,
  SectionTitle, Step, Steps, Trouble, UiLabel,
} from './parts'
import { ChannelRowMock, ConnectButtonsMock, ConnectFormMock } from './postMocks'

export function XGuide() {
  return (
    <div className="space-y-5">
      <Callout kind="info" title="X の接続は「自分のキー」で行います">
        <p>
          X の開発者サイトで自分専用のアプリを作り、そこで発行した <b>キー4つ</b> を Genemo Post に貼ります。
          API の利用料や回数の上限は、お店自身の開発者アカウントの条件で決まります。
        </p>
      </Callout>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">準備するもの</h3>
          <Checklist items={[
            '投稿したい X アカウント（そのアカウントでログインしておく）',
            'X の開発者アカウント（初めてなら開発者サイトで登録します）',
            '所要時間の目安: 15分',
          ]} />
        </div>
        <div className="gm-card space-y-2 p-4">
          <h3 className="text-sm font-bold">知っておくこと</h3>
          <Checklist items={[
            'X の API の利用条件や料金は X 側で決まり、変わることがあります。開発者サイトでご確認ください。',
            'キーに有効期限はありません。X 側でアプリを消したり、キーを作り直したりすると使えなくなります。',
            '本文は 280 字（日本語は 140 字）までです。',
          ]} />
        </div>
      </div>

      <Steps>
        <Step n={1} title="開発者サイトにログインし、アプリを開く"
          mock={
            <BrowserFrame url="developer.x.com/en/portal/projects-and-apps">
              <MockSidebarLayout menu={['Dashboard', 'Projects & Apps', 'Products', 'Billing']} active="Projects & Apps">
                <div className="font-bold">Projects & Apps</div>
                <MockRow><span className="font-bold">Default project</span></MockRow>
                <Mark n={1} block><MockRow><span>📦 genemo-post-shop</span><span className="ml-auto text-ink-muted">App</span></MockRow></Mark>
                <MockButton>+ Add App</MockButton>
              </MockSidebarLayout>
            </BrowserFrame>
          }>
          <p>
            投稿したい X アカウントでログインした状態で、X の開発者サイト（<Code>developer.x.com</Code>。新しい画面では <Code>console.x.com</Code>）を開きます。
            初めての場合は、利用目的を英語で短く書いて開発者登録をします（例: <i>Post my shop's updates from my own scheduling tool.</i>）。
          </p>
          <p>登録すると、最初のアプリが1つできています。<UiLabel en="Projects & Apps">プロジェクトとアプリ</UiLabel> からそのアプリを開きます。</p>
        </Step>

        <Step n={2} title="アプリの権限を「読み取りと書き込み」にする"
          mock={
            <BrowserFrame url="developer.x.com/…/apps/…/auth-settings">
              <div className="space-y-2">
                <div className="font-bold">User authentication settings</div>
                <div className="text-[11px] text-ink-muted">App permissions</div>
                <div className="space-y-1">
                  <div>○ Read</div>
                  <Mark n={2} block><div className="px-1 font-bold">● Read and write</div></Mark>
                  <div>○ Read and write and Direct message</div>
                </div>
                <div className="text-[11px] text-ink-muted">Type of App</div>
                <div className="font-bold">● Web App, Automated App or Bot</div>
                <MockInput label="Callback URI / Redirect URL" value="https://post.genemo.jp" />
                <MockInput label="Website URL" value="https://post.genemo.jp" />
                <MockButton kind="primary">Save</MockButton>
              </div>
            </BrowserFrame>
          }>
          <p>
            アプリの <UiLabel en="Settings">設定</UiLabel> にある <UiLabel en="User authentication settings">ユーザー認証の設定</UiLabel> の
            <UiLabel en="Set up / Edit">設定する</UiLabel> を押します。
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li><b>App permissions</b> は <UiLabel>Read and write</UiLabel> を選びます。<b>ここが Read のままだと投稿できません。</b></li>
            <li><b>Type of App</b> は <UiLabel>Web App, Automated App or Bot</UiLabel> を選びます。</li>
            <li>
              <b>Callback URI</b> と <b>Website URL</b> は入力が必須です。Genemo Post はここを使わないので、
              <Code>https://post.genemo.jp</Code> を入れて構いません（自分のお店のサイトでも大丈夫です）。
            </li>
          </ul>
          <p>最後に <UiLabel>Save</UiLabel> を押します。</p>
        </Step>

        <Step n={3} title="キー4つを発行してメモする"
          mock={
            <BrowserFrame url="developer.x.com/…/apps/…/keys">
              <div className="space-y-2">
                <div className="font-bold">Keys and tokens</div>
                <div className="rounded-md border border-line p-2">
                  <div className="font-bold">Consumer Keys</div>
                  <div className="mt-1 flex items-center gap-2"><span className="text-ink-muted">API Key and Secret</span>
                    <span className="ml-auto"><Mark n={3}><MockButton>Regenerate</MockButton></Mark></span></div>
                </div>
                <div className="rounded-md border border-line p-2">
                  <div className="font-bold">Authentication Tokens</div>
                  <div className="mt-1 flex items-center gap-2"><span className="text-ink-muted">Access Token and Secret</span>
                    <span className="ml-auto"><Mark n={4}><MockButton>Generate</MockButton></Mark></span></div>
                  <div className="mt-1 text-[11px] text-ok">Created with Read and Write permissions</div>
                </div>
              </div>
            </BrowserFrame>
          }>
          <p>アプリの <UiLabel en="Keys and tokens">キーとトークン</UiLabel> タブを開きます。</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li><b>Consumer Keys</b> の <UiLabel>Regenerate</UiLabel> で <b>API Key</b> と <b>API Key Secret</b> を表示してコピーします。</li>
            <li><b>Authentication Tokens</b> の <UiLabel>Generate</UiLabel> で <b>Access Token</b> と <b>Access Token Secret</b> を表示してコピーします。</li>
          </ol>
          <Callout kind="warn" title="手順2より前に作ったトークンは使えません">
            Access Token は「作ったときの権限」を覚えています。権限を Read and write に変える前に作ったものは、
            <UiLabel>Regenerate</UiLabel> で作り直してください。表示が <b>Read and Write</b> になっていれば大丈夫です。
          </Callout>
          <Callout kind="secret">
            キーは画面を閉じると二度と表示されません。他人に見せたり、チャットやメールに貼ったりしないでください。漏れたら Regenerate で作り直せます。
          </Callout>
        </Step>

        <Step n={4} title="Genemo Post に貼って接続する"
          mock={
            <div className="space-y-3">
              <ConnectButtonsMock highlight="X を接続（自分のキー）" n={5} />
              <ConnectFormMock n={6} fields={[
                { label: 'API Key', value: 'xxxxxxxxxxxxxxxxxxxxxxxxx' },
                { label: 'API Secret', value: 'xxxxxxxxxxxxxxxxxxxxxxxxx', secret: true },
                { label: 'Access Token', value: 'xxxxxxxxxxxxxxxxxxxxxxxxx', secret: true },
                { label: 'Access Token Secret', value: 'xxxxxxxxxxxxxxxxxxxxxxxxx', secret: true },
              ]} />
            </div>
          }>
          <p>
            Genemo Post の <UiLabel>ペルソナ</UiLabel> を開き、投稿させたいペルソナの <UiLabel>X を接続（自分のキー）</UiLabel> を押します。
          </p>
          <p>
            4つの欄に、手順3でコピーしたキーをそれぞれ貼り、<UiLabel>接続する</UiLabel> を押します。
            Genemo Post が X に問い合わせて、キーが正しいかをその場で確かめます。
          </p>
        </Step>

        <Step n={5} title="「接続中」になれば完了"
          mock={<ChannelRowMock platform="X (BYOK)" handle="@genemo_coffee" />}>
          <p>X の行に <span className="gm-chip gm-chip-ok">接続中</span> と、アカウント名が出れば完了です。</p>
          <p>
            右の <UiLabel>投稿方針</UiLabel> で、投稿の出し方を選べます。最初は <b>承認したものだけ投稿</b>（承認キューで確認してから出す）がおすすめです。
          </p>
        </Step>
      </Steps>

      <SectionTitle>うまくいかないとき</SectionTitle>
      <Trouble items={[
        { symptom: '「X の資格確認に失敗しました」と出る', fix: 'キーの貼り間違いが多いです。前後の空白や、API Key と Access Token の入れ違いがないか確かめてください。作り直した後は、古いキーは使えません。' },
        { symptom: '接続はできたのに、投稿が失敗する', fix: 'Access Token が Read のときに作られている可能性があります。手順2で Read and write にしてから、手順3で Access Token を作り直し、「X を接続（自分のキー）」でもう一度接続してください。' },
        { symptom: '状態が「認証エラー」になった', fix: 'X 側でアプリやキーが無効になっています。キーを作り直して、もう一度接続してください。' },
      ]} />
    </div>
  )
}
